import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import kebabCase from 'lodash/kebabCase'
import Mixin from 'components/common/Mixin'
import Columns from 'components/common/Columns/Columns'
import Link from 'components/common/Link/Link'
import Theme from 'components/common/Theme/Theme'
import Text, { H1, H2 } from 'components/common/Text/Text'
import Media from 'components/common/Media'
import VehicleImage from 'components/common/VehicleImage'
import { filterProps } from 'helpers/utils'
// import Records from 'components/BrandLandingPage/components/Records'
import MetaHelmet from './components/MetaHelmet'
import SchemasHelmet from './components/SchemasHelmet'
import {
  YMME_DESCRIPTION_ID,
  YMME_HEADER_ID,
  YMME_PAGE_IMAGE_HEIGHT,
  YMME_PAGE_IMAGE_HEIGHT_MOBILE,
  YMME_PAGE_IMAGE_WIDTH,
  YMME_PAGE_IMAGE_WIDTH_MOBILE
} from './constants'

const IndexableYMME = ({
  title,
  description,
  subheading,
  // brandsSubheading,
  columnWidth,
  list,
  groupedList = [],
  vehicleMaker,
  vehicleMakerFormmatted,
  vehicleModel,
  vehicleModelFormmatted,
  vehicleYear,
  selectorSubheading,
  imageSrc,
  canonicalUrl,
  metaDescription,
  metaTitle,
  schemasName
  // records
}) => (
  <Wrapper>
    {!!vehicleMaker && (
      <>
        <MetaHelmet
          metaTitle={ metaTitle }
          metaDescription={ metaDescription }
          imageSrc={ imageSrc }
          canonicalUrl={ canonicalUrl } />

        <SchemasHelmet
          make={ vehicleMakerFormmatted }
          model={ vehicleModelFormmatted }
          year={ vehicleYear }
          imageSrc={ imageSrc }
          canonicalUrl={ canonicalUrl }
          schemasName={ schemasName }
          speakableIds={ [`#${YMME_DESCRIPTION_ID}`, `#${YMME_HEADER_ID}`] }
          description={ metaDescription } />
      </>
    )}

    <Title data-at-heading={ kebabCase(title) } block headline centered weight="600" id={ YMME_HEADER_ID }>
      { title }
    </Title>
    {!!vehicleYear && (
      <CarWrapper>
        <VehicleImage
          make={ vehicleMaker }
          model={ vehicleModel }
          year={ Number(vehicleYear) }
          height={ YMME_PAGE_IMAGE_HEIGHT }
          width={ YMME_PAGE_IMAGE_WIDTH }
          mobileHeight={ YMME_PAGE_IMAGE_HEIGHT_MOBILE }
          mobileWidth={ YMME_PAGE_IMAGE_WIDTH_MOBILE } />
      </CarWrapper>
    )}
    <Subheading>{ subheading }</Subheading>
    <Desc data-at-shop-by-vehicle block centered size="20" id={ YMME_DESCRIPTION_ID }>
      { description }
    </Desc>
    <SelectorSubheading>{ selectorSubheading }</SelectorSubheading>
    <Media.Desktop>
      <Columns width={ columnWidth } adaptive className={ groupedList.length === 2 ? 'table' : '' }>
        {
          groupedList.map((list, index) =>
            <StyledUl key={ index } >
              {
                list.map(({ link, name }, index2) =>
                  <StyledListItem key={ index2 }>
                    <StyledLink button to={ link } onlyOneItem={ groupedList.length === 1 }>
                      <StyledText>{ name }</StyledText>
                    </StyledLink>
                  </StyledListItem>)
              }
            </StyledUl>)
        }
      </Columns>
    </Media.Desktop>
    <Media.Mobile>
      <StyledUl onlyOneItem={ groupedList.length === 1 }>
        {
          list.map(({ link, name }) =>
            <StyledListItem key={ name }>
              <StyledLink button to={ link }>
                <StyledText>{ name }</StyledText>
              </StyledLink>
            </StyledListItem>)
        }
      </StyledUl>
    </Media.Mobile>
    {/* TODO Implement records (brandslist) for the trim selection page */}
    {/* {!!vehicleYear && (
      <>
        <Subheading>{ brandsSubheading }</Subheading>
        <RecordsWrapper>
          <Records records={ records } />
        </RecordsWrapper>
      </>
    )} */}
  </Wrapper>
)

IndexableYMME.propTypes = {
  // brandsSubheading: PropTypes.string,
  canonicalUrl: PropTypes.string,
  columnWidth: PropTypes.string,
  description: PropTypes.string,
  groupedList: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape())),
  imageSrc: PropTypes.string,
  list: PropTypes.arrayOf(PropTypes.shape()),
  metaDescription: PropTypes.string,
  metaTitle: PropTypes.string,
  schemasName: PropTypes.string,
  selectorSubheading: PropTypes.string,
  subheading: PropTypes.string,
  title: PropTypes.string,
  vehicleMaker: PropTypes.string,
  vehicleMakerFormmatted: PropTypes.string,
  vehicleModel: PropTypes.string,
  vehicleModelFormmatted: PropTypes.string,
  vehicleYear: PropTypes.string
}

export default IndexableYMME

const Title = styled(H1)`
  text-align: center;
  ${Mixin.font.GothamBold(34, 600, 30)}
  ${Mixin.responsive('font-size', '34px', '26px')}
`

const Subheading = styled(H2)`
  text-transform: uppercase;
  text-align: center;
  ${Mixin.responsive('margin-top', '30px', '40px')};
  ${Mixin.font.GothamBold(26, 600, 22)}
  ${Media.mobile`
    font-size: 22px;
  `}
`

const Desc = styled(Text)`
  margin-top: 15px;
  margin-bottom: 35px;
  font-size: 14px;
  ${Media.mobile`
    font-size: 16px;
  `}
`

const SelectorSubheading = styled.p`
  margin-bottom: 30px;
  font-weight: 700;
  font-size: 24px;
  text-align: center;
  ${Media.mobile`
    font-size: 18px;
  `}
`

const CarWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

const StyledUl = styled(props => <ul { ...filterProps(props, ['onlyOneItem']) } />)`
  padding: 0;
  margin: 0;
  ${Media.mobile`
    display: flex;
    flex-wrap: wrap;
    justify-content: left;
    ${props => props.onlyOneItem && css`
      justify-content: center;
    `}
  `}
`

const StyledLink = styled(Link)`
  margin: 0 auto;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  color: ${Theme.colors.black};
  background-color: ${Theme.colors.white};
  ${Mixin.responsive('width', '80%', '100%')};
  height: 48px;
  ${Media.desktop`
    max-width: 350px;
  `}
`

const Wrapper = styled.div`
  margin: 40px auto;
  ${Mixin.responsive('padding', '0', '0 10px')}

  .table {
    width: 60%;
    margin: 0 auto;
  }
`

const StyledText = styled(Text)`
  font-weight: 600;
  ${Media.mobile`
    font-size: 12px;
    line-height: 15px;
  `}
`

const StyledListItem = styled.li`
  margin-bottom: 40px;
  ${Media.mobile`
    margin: 0;
    width: 50%;
    padding: 12px 5px;
  `}
`

// const RecordsWrapper = styled.div`
//   display: flex;
//   justify-content: center;
//   flex-wrap: wrap;
// `
